import { useState } from "react";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useLoadNotes, useMyCompanyName } from "@/hooks/useLoadNotes";

/**
 * Compact "Add Note" tile for the dispatch info-card row. Writing happens in a
 * dialog so the card stays the same height as the ETA / tracking tiles; the
 * stored note history lives in its own full-width panel below.
 */
interface Props {
  loadId: string;
  viewerRole: "shipper" | "broker";
  viewerUserId: string;
  fallbackCompanyName?: string | null;
  noteCount: number;
  cardClassName?: string;
}

export function AddLoadNoteCard({
  loadId, viewerRole, viewerUserId, fallbackCompanyName, noteCount, cardClassName,
}: Props) {
  const { addNote } = useLoadNotes(loadId);
  const { data: myCompanyName } = useMyCompanyName(viewerUserId);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const counterparty = viewerRole === "shipper" ? "broker" : "shipper";

  const post = async () => {
    if (!text.trim() || addNote.isPending) return;
    try {
      await addNote.mutateAsync({
        body: text,
        authorUserId: viewerUserId,
        authorRole: viewerRole,
        authorCompanyName: myCompanyName ?? fallbackCompanyName ?? null,
      });
      setText("");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Could not post note");
    }
  };

  return (
    <>
      <Card className={`p-3 ${cardClassName ?? ""}`}>
        <div className="flex items-center gap-2 mb-2 min-w-0">
          <MessageSquarePlus className="h-4 w-4 text-brand-teal shrink-0" />
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
            Load Notes
          </div>
        </div>
        <div className="text-sm font-semibold text-foreground">
          {noteCount} {noteCount === 1 ? "note" : "notes"}
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Shared with the {counterparty}.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="h-7 mt-2 w-full gap-1.5 text-xs"
          onClick={() => setOpen(true)}
        >
          <MessageSquarePlus className="h-3.5 w-3.5" /> Add Note
        </Button>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { if (!addNote.isPending) setOpen(o); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add a load note</DialogTitle>
            <DialogDescription>
              Both you and the {counterparty} will see this note on this load.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
            rows={4}
            autoFocus
            placeholder="Status update, instructions, gate code…"
            className="text-sm resize-none"
          />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={addNote.isPending}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-brand-teal hover:bg-brand-teal/90 text-white"
              onClick={post}
              disabled={!text.trim() || addNote.isPending}
            >
              {addNote.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Post note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AddLoadNoteCard;
